import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Box,
  Typography,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import React from "react";
import { formatCurrency } from "../../utils/formatters";
import { DayBookEmptyState } from "./daybook.emptystate";
import { formatOrderId } from "./utils/id-formatter";
import { DaybookOrder, PanelProps } from "./types";
import { getPanelStyles } from "./styles";

const OrderStatusChip: React.FC<{ status: DaybookOrder["order_status"] }> = ({
  status,
}) => (
  <Chip
    label={status}
    color={
      status === "completed"
        ? "success"
        : status === "cancelled"
        ? "error"
        : "warning"
    }
    size="small"
    variant="outlined"
    sx={{
      height: 24,
      textTransform: "capitalize",
      fontWeight: 500,
      '&.MuiChip-colorSuccess': {
        color: '#1E8078',
        borderColor: alpha('#1E8078', 0.3),
        backgroundColor: alpha('#1E8078', 0.05),
      }
    }}
  />
);

const LoadingSkeleton: React.FC = () => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Order ID</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Total Cost</TableCell>
          <TableCell align="right">Total Price</TableCell>
          <TableCell align="right">Revenue</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[1, 2, 3, 4, 5].map((i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell>
              <Skeleton variant="rounded" width={80} height={24} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={80} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmptyState: React.FC = () => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Order ID</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Total Cost</TableCell>
          <TableCell align="right">Total Price</TableCell>
          <TableCell align="right">Revenue</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell colSpan={5} sx={{ border: "none", p: 0 }}>
            <DayBookEmptyState
              icon="receipt_long"
              title="No Orders Found"
              message="This session has no orders yet. Orders will appear here once they are created."
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

const OrderRow: React.FC<{ order: DaybookOrder }> = ({ order }) => {
  const theme = useTheme();
  return (
    <TableRow 
      sx={{ 
        '&:hover': { 
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          cursor: 'pointer'
        },
        transition: 'background-color 0.2s ease'
      }}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
          {formatOrderId(order.order_id)}
        </Typography>
      </TableCell>
      <TableCell>
        <OrderStatusChip status={order.order_status} />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(order.order_total_cost)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(order.order_total_price)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            color: order.order_total_revenue > 0 ? '#1E8078' : 'text.secondary'
          }}
        >
          {formatCurrency(order.order_total_revenue)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const DayBookOrdersPanel: React.FC<PanelProps> = ({ loading, data }) => {
  const theme = useTheme();
  const styles = getPanelStyles(theme);

  if (loading) return <LoadingSkeleton />;
  if (data.length === 0) return <EmptyState />;

  return (
    <Box sx={styles.container}>
      <TableContainer sx={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Order ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Total Cost</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Total Price</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Revenue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((order: DaybookOrder) => (
              <OrderRow key={order.order_id} order={order} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
